import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Btn } from "@/components/ui/Btn/Btn";
import { Input } from "@/components/ui/Input/Input";
import { Modal } from "@/components/ui/Modal/Modal";
import { Field } from "@/components/auth/FormShell";
import { useCreateBrand } from "@/lib/queries";
import type { BrandBrain } from "@notamen/core/brand";
import styles from "./CreateBrandModal.module.css";

const KINDS: Array<{ value: BrandBrain["kind"]; label: string; hint: string }> = [
  { value: "ecommerce", label: "E-commerce", hint: "Sells products — Shopify, Woo, etc." },
  { value: "service", label: "Service", hint: "Offers services or bookings." },
  { value: "creator", label: "Creator", hint: "Personal brand / influencer." },
  { value: "media", label: "Media", hint: "Publication, show, or newsletter." },
];

interface CreateBrandModalProps {
  wsId: string;
  open: boolean;
  onClose: () => void;
}

export function CreateBrandModal({ wsId, open, onClose }: CreateBrandModalProps) {
  const navigate = useNavigate();
  const createBrand = useCreateBrand(wsId);
  const [name, setName] = useState("");
  const [kind, setKind] = useState<BrandBrain["kind"]>("ecommerce");
  const [elevator, setElevator] = useState("");
  const [audiencePrimary, setAudiencePrimary] = useState("");

  const canSubmit = !!name.trim() && !!elevator.trim() && !!audiencePrimary.trim();

  function reset() {
    setName("");
    setKind("ecommerce");
    setElevator("");
    setAudiencePrimary("");
    createBrand.reset();
  }

  async function onSubmit() {
    if (!canSubmit) return;
    const result = await createBrand.mutateAsync({
      name: name.trim(),
      kind,
      elevator: elevator.trim(),
      audiencePrimary: audiencePrimary.trim(),
    });
    reset();
    onClose();
    navigate(`/w/${wsId}/b/${result.brand.id}`);
  }

  function handleClose() {
    reset();
    onClose();
  }

  return (
    <Modal
      open={open}
      title="New brand"
      onClose={handleClose}
      footer={
        <>
          <Btn variant="secondary" onClick={handleClose}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            onClick={onSubmit}
            disabled={!canSubmit}
            loading={createBrand.isPending}
          >
            Create brand
          </Btn>
        </>
      }
    >
      <div className={styles.form}>
        {createBrand.error && (
          <p className={styles.error}>{createBrand.error.message}</p>
        )}
        <Field label="Brand name" htmlFor="brand-name">
          <Input
            id="brand-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your brand's name"
            autoFocus
          />
        </Field>

        <Field label="Brand kind">
          <div className={styles.kinds}>
            {KINDS.map((k) => (
              <button
                key={k.value}
                type="button"
                className={`${styles.kind} ${kind === k.value ? styles.kindActive : ""}`}
                onClick={() => setKind(k.value)}
              >
                <span className={styles.kindLabel}>{k.label}</span>
                <span className={styles.kindHint}>{k.hint}</span>
              </button>
            ))}
          </div>
        </Field>

        <Field
          label="One-line elevator pitch"
          hint="What the brand is, in a sentence. You'll flesh out the full brain next."
          htmlFor="brand-elevator"
        >
          <Input
            id="brand-elevator"
            value={elevator}
            onChange={(e) => setElevator(e.target.value)}
            placeholder="What you make and who it's for, in one sentence."
          />
        </Field>

        <Field label="Primary audience" htmlFor="brand-audience">
          <Input
            id="brand-audience"
            value={audiencePrimary}
            onChange={(e) => setAudiencePrimary(e.target.value)}
            placeholder="Who you're talking to and what they care about."
          />
        </Field>
      </div>
    </Modal>
  );
}
